/**
 * AdminClientErrorsPage — browse client-side errors sorted by frequency.
 *
 * Sources rows from /api/admin/client-errors. Every row is a
 * (message, error_type) signature aggregated across the selected window,
 * so a single bug that crashed 400 users shows up once with count=400
 * instead of drowning the list. Row click opens a drawer with the most
 * recent 20 raw occurrences (stack, component stack, URL, user, IP).
 *
 * The underlying collection has a 30-day TTL, so this page is inherently
 * bounded — no manual retention controls needed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  ArrowLeft, AlertTriangle, Search, RefreshCw, Loader2, Users, Fingerprint,
  Globe, Clock, Bug, ServerCrash, ChevronRight, Copy, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WINDOW_OPTIONS = [
  { value: '1h',  label: 'Last 1 hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d',  label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All (up to TTL)' },
];

const TYPE_OPTIONS = [
  { value: 'all',            label: 'All types' },
  { value: 'react_boundary', label: 'React crashes' },
  { value: 'api_5xx',        label: 'API 5xx failures' },
];

const ROLE_OPTIONS = [
  { value: 'all',       label: 'All roles' },
  { value: 'admin',     label: 'Admin' },
  { value: 'sub_admin', label: 'Sub-admin' },
  { value: 'paid_user', label: 'Paid user' },
  { value: 'free',      label: 'Free' },
  { value: 'anonymous', label: 'Anonymous (not signed in)' },
];

const formatRelative = (iso) => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 60)       return `${s}s ago`;
  if (s < 3600)     return `${Math.round(s / 60)}m ago`;
  if (s < 86400)    return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const truncate = (str, n) =>
  !str ? '' : str.length <= n ? str : `${str.slice(0, n - 1)}…`;

const typeBadge = (t) =>
  t === 'api_5xx'
    ? { label: '5xx', icon: ServerCrash, className: 'bg-orange-100 text-orange-800 border-orange-200' }
    : { label: 'React', icon: Bug, className: 'bg-rose-100 text-rose-800 border-rose-200' };

const AdminClientErrorsPage = () => {
  const navigate = useNavigate();

  // Filters
  const [window, setWindow] = useState('7d');
  const [errorType, setErrorType] = useState('all');
  const [role, setRole] = useState('all');
  const [search, setSearch] = useState('');

  // Data
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [uniqueSigs, setUniqueSigs] = useState(0);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSig, setDrawerSig] = useState(null);
  const [occLoading, setOccLoading] = useState(false);
  const [occurrences, setOccurrences] = useState([]);

  const loadSignatures = useCallback(async (silent = false) => {
    try {
      if (silent) setRefreshing(true); else setLoading(true);
      const params = { window, limit: 100 };
      if (errorType !== 'all') params.error_type = errorType;
      if (role !== 'all')      params.role = role;
      if (search.trim())       params.search = search.trim();

      const res = await axios.get(`${API}/admin/client-errors`, { params });
      setSignatures(res.data?.signatures || []);
      setTotalEvents(res.data?.total_events || 0);
      setUniqueSigs(res.data?.unique_signatures || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load client errors');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [window, errorType, role, search]);

  useEffect(() => { loadSignatures(); /* eslint-disable-line */ }, [window, errorType, role]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    loadSignatures();
  };

  const openDrawer = async (sig) => {
    setDrawerSig(sig);
    setDrawerOpen(true);
    setOccurrences([]);
    setOccLoading(true);
    try {
      const params = { message: sig.message, window, limit: 20 };
      if (sig.error_type) params.error_type = sig.error_type;
      const res = await axios.get(`${API}/admin/client-errors/occurrences`, { params });
      setOccurrences(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load occurrences');
    } finally {
      setOccLoading(false);
    }
  };

  const copy = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error('Clipboard unavailable');
    }
  };

  const downloadCsv = async (mode = 'signatures') => {
    // Streams the CURRENT filter (window + type + role + search) as CSV.
    // We use axios (not window.open) so the Authorization header goes with
    // the request; the endpoint is admin-gated.
    try {
      const params = { window, mode, limit: mode === 'signatures' ? 1000 : 5000 };
      if (errorType !== 'all') params.error_type = errorType;
      if (role !== 'all')      params.role = role;
      if (search.trim())       params.search = search.trim();

      const res = await axios.get(`${API}/admin/client-errors/export/csv`, {
        params,
        responseType: 'blob',
      });
      // Grab the filename the server suggested (falls back to a sensible default).
      const disp = res.headers?.['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(disp);
      const filename = match ? match[1] : `client-errors-${mode}.csv`;

      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export CSV');
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Back button (matches sibling admin log pages).
            Breadcrumb trail lives in the global header via Layout.js. */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/tools')}
            className="text-gray-600"
            data-testid="client-errors-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Tools
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-rose-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Client Errors</h1>
              <p className="text-gray-600">
                Aggregated React crashes and API 5xx failures — auto-purged after 30 days.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadCsv('signatures')}
              disabled={loading}
              data-testid="client-errors-export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadSignatures(true)}
              disabled={refreshing || loading}
              data-testid="client-errors-refresh-btn"
            >
              {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card data-testid="stat-total-events">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Total events</div>
              <div className="text-3xl font-semibold text-slate-900 mt-1">
                {loading ? '—' : totalEvents.toLocaleString()}
              </div>
              <div className="text-xs text-slate-400 mt-1">in the selected window</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-unique-sigs">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Unique signatures</div>
              <div className="text-3xl font-semibold text-slate-900 mt-1">
                {loading ? '—' : uniqueSigs.toLocaleString()}
              </div>
              <div className="text-xs text-slate-400 mt-1">distinct error messages</div>
            </CardContent>
          </Card>
          <Card data-testid="stat-top-count">
            <CardContent className="p-4">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Most frequent</div>
              <div className="text-3xl font-semibold text-slate-900 mt-1">
                {loading || signatures.length === 0 ? '—' : signatures[0].count.toLocaleString()}
              </div>
              <div className="text-xs text-slate-400 mt-1">
                {loading || signatures.length === 0 ? 'no errors' : 'occurrences of the top signature'}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Select value={window} onValueChange={setWindow}>
                <SelectTrigger data-testid="filter-window"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WINDOW_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={errorType} onValueChange={setErrorType}>
                <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="filter-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <form onSubmit={onSearchSubmit} className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search message / URL / ERR-…"
                  className="pl-9"
                  data-testid="filter-search-input"
                />
              </form>
            </div>
          </CardContent>
        </Card>

        {/* Signatures list */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Signatures{loading ? '' : ` (${signatures.length})`}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 flex items-center justify-center text-slate-500">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…
              </div>
            ) : signatures.length === 0 ? (
              <div className="p-12 text-center text-slate-500" data-testid="client-errors-empty">
                <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <div className="text-sm">No client errors in this window. Nice.</div>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {signatures.map((sig, idx) => {
                  const tb = typeBadge(sig.error_type);
                  const TIcon = tb.icon;
                  return (
                    <button
                      key={`${sig.error_type}::${sig.message}::${idx}`}
                      type="button"
                      onClick={() => openDrawer(sig)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-start gap-3"
                      data-testid={`client-error-row-${idx}`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        <Badge variant="outline" className={`${tb.className} border`}>
                          <TIcon className="w-3 h-3 mr-1" />
                          {tb.label}
                        </Badge>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-sm text-slate-800 truncate">
                          {truncate(sig.message, 200)}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatRelative(sig.last_seen)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {sig.affected_users} user{sig.affected_users === 1 ? '' : 's'}
                          </span>
                          {sig.roles.length > 0 && (
                            <span className="inline-flex items-center gap-1">
                              <Fingerprint className="w-3 h-3" />
                              {sig.roles.join(', ')}
                            </span>
                          )}
                          {sig.latest_url && (
                            <span className="inline-flex items-center gap-1 truncate max-w-[280px]">
                              <Globe className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{sig.latest_url}</span>
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 flex items-center gap-2">
                        <div className="text-right">
                          <div className="text-xl font-semibold text-slate-900 tabular-nums">
                            {sig.count.toLocaleString()}
                          </div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">events</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail drawer */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="sm:max-w-2xl w-full overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="pr-8">Error occurrences</SheetTitle>
            <SheetDescription className="font-mono text-xs break-words">
              {drawerSig?.message}
            </SheetDescription>
          </SheetHeader>

          {drawerSig && (
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <Badge variant="outline" className={typeBadge(drawerSig.error_type).className}>
                {typeBadge(drawerSig.error_type).label}
              </Badge>
              <Badge variant="outline">{drawerSig.count.toLocaleString()} events</Badge>
              <Badge variant="outline">{drawerSig.affected_users} users</Badge>
              <Badge variant="outline">First: {formatRelative(drawerSig.first_seen)}</Badge>
              <Badge variant="outline">Last: {formatRelative(drawerSig.last_seen)}</Badge>
            </div>
          )}

          <div className="mt-6">
            {occLoading ? (
              <div className="p-8 flex items-center justify-center text-slate-500">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading occurrences…
              </div>
            ) : occurrences.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No occurrences in this window.</div>
            ) : (
              <div className="space-y-3">
                {occurrences.map((occ, idx) => (
                  <div
                    key={`${occ.error_id || 'noid'}-${idx}`}
                    className="border border-slate-200 rounded-md p-3 bg-slate-50/50"
                    data-testid={`occurrence-${idx}`}
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <code className="text-xs font-mono px-2 py-0.5 rounded bg-white border border-slate-200 text-slate-700">
                        {occ.error_id || '—'}
                      </code>
                      <div className="text-xs text-slate-500">
                        {formatRelative(occ.server_timestamp)} · {new Date(occ.server_timestamp).toLocaleString()}
                      </div>
                      {occ.error_id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => copy(occ.error_id, 'Error ID copied')}
                        >
                          <Copy className="w-3 h-3 mr-1" /> Copy ID
                        </Button>
                      )}
                    </div>

                    <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-slate-600">
                      {occ.url && (
                        <div><span className="text-slate-400">URL:</span> <span className="font-mono break-all">{occ.url}</span></div>
                      )}
                      <div>
                        <span className="text-slate-400">User:</span>{' '}
                        <span className="font-mono">{occ.user_id || 'anonymous'}</span>
                        {occ.user_role && <span className="ml-1 text-slate-400">({occ.user_role})</span>}
                      </div>
                      {occ.ip && (
                        <div><span className="text-slate-400">IP:</span> <span className="font-mono">{occ.ip}</span></div>
                      )}
                      {occ.user_agent && (
                        <div className="truncate"><span className="text-slate-400">UA:</span> <span className="font-mono">{occ.user_agent}</span></div>
                      )}
                    </div>

                    {(occ.stack || occ.component_stack) && (
                      <details className="mt-2">
                        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                          Stack trace
                        </summary>
                        {occ.stack && (
                          <pre className="mt-2 text-[10px] font-mono bg-white border border-slate-200 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-words text-slate-700">
                            {occ.stack}
                          </pre>
                        )}
                        {occ.component_stack && (
                          <>
                            <div className="mt-2 text-[10px] uppercase tracking-wide text-slate-400">Component stack</div>
                            <pre className="mt-1 text-[10px] font-mono bg-white border border-slate-200 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-words text-slate-700">
                              {occ.component_stack}
                            </pre>
                          </>
                        )}
                      </details>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </Layout>
  );
};

export default AdminClientErrorsPage;
